<template>

    <FormSection
        :is_border="true"
        form_class="grid gap-y-4 gap-x-10 w-full overflow-x-scroll"
        form_style="grid-template-columns: repeat(8, minmax(auto, min-content)) auto">

        <template #title>

            <font-awesome-icon class="text-blue mr-6"
                style="width: 27px; height: 27px"
                :icon="['fas', 'user']">
            </font-awesome-icon>

            <div class="text-3xl font-nexa text-blue">
                {{ __('Users') }}
            </div>

        </template>


        <template #form>

            <div class="col-span-1 whitespace-nowrap">{{ __('Name') }}</div>

            <div class="col-span-1 whitespace-nowrap">{{ __('Email') }}</div>

            <div class="col-span-1 whitespace-nowrap">{{ __('Role') }}</div>

            <div class="col-span-1 whitespace-nowrap">{{ __('Member since') }}</div>

            <div class="col-span-1 whitespace-nowrap">{{ __('Language') }}</div>

            <div class="col-span-1 whitespace-nowrap">{{ __('Setup (:current / :total)', {
                current: 2,
                total: 4
            }) }}</div>

            <div class="col-span-1 whitespace-nowrap">{{ __('Completed exercises') }}</div>

            <div class="col-span-1 whitespace-nowrap">{{ __('Favorites') }}</div>

            <div class="col-span-1 whitespace-nowrap"></div>


            <template v-for="member in members"
                :key="member.id">


                <div class="col-span-1 whitespace-nowrap">{{ member.name }}</div>


                <div class="col-span-1 whitespace-nowrap">{{ member.email }}</div>


                <button class="col-span-1 cursor-pointer text-sm text-gray-400 underline text-left whitespace-nowrap"
                    @click="manageRole(member)"
                    v-if="availableRoles.length">
                    {{ displayableRole(member.membership.role) }}
                </button>


                <div class="col-span-1 whitespace-nowrap">{{ format_created_at( member.membership.created_at ) }}</div>


                <div class="col-span-1 whitespace-nowrap">{{ format_language( member.language ) }}</div>


                <div class="col-span-1 whitespace-nowrap">

                    <template v-if="member.startup_completed_at">

                        <font-awesome-icon class="text-green mr-4 whitespace-nowrap"
                            :icon="['fas', 'check']">
                        </font-awesome-icon>

                        <span class="text-green text-sm">{{ __('Done') }}</span>

                    </template>

                    <template v-else>

                        <font-awesome-icon class="text-blue mr-4 whitespace-nowrap"
                            :icon="['fas', 'clock']">
                        </font-awesome-icon>

                        <span class="text-blue text-sm">{{ __('Waiting') }}</span>

                    </template>

                </div>


                <div class="col-span-1 whitespace-nowrap">{{ member.target_steps_count }}</div>


                <div class="col-span-1 whitespace-nowrap">{{ member.exercises_count }}</div>


                <button class="col-span-1 cursor-pointer text-sm text-red text-right whitespace-nowrap"
                    @click="confirmTeamMemberRemoval(member)">

                    <span class="border border-red px-1.5 py-0.5 hover:bg-red hover:text-white">

                        {{ __('Remove') }}

                    </span>

                </button>

            </template>

        </template>

    </FormSection>


    <dialog-modal :show="currentlyManagingRole" @close="currentlyManagingRole = false">

        <template #title>
            {{ __('Manage Role') }}
        </template>


        <template #content>

            <div v-if="managingRoleFor">

                <div class="relative z-0 mt-1 border border-gray-200 rounded-lg cursor-pointer">

                    <button class="relative px-4 py-3 inline-flex w-full rounded-lg focus:z-10 focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-200"
                        :class="{'border-t border-gray-200 rounded-t-none': i > 0, 'rounded-b-none': i !== Object.keys(availableRoles).length - 1}"
                        type="button"
                        @click="updateRoleForm.role = role.key"
                        v-for="(role, i) in availableRoles"
                        :key="role.key">


                        <div :class="{'opacity-50': updateRoleForm.role && updateRoleForm.role !== role.key}">

                            <div class="flex items-center">
                                <div class="text-sm text-gray-600"
                                     :class="{'font-semibold': updateRoleForm.role === role.key}">
                                    {{ __(role.name) }}
                                </div>

                                <svg v-if="updateRoleForm.role === role.key" class="ml-2 h-5 w-5 text-green-400"
                                     fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                     stroke="currentColor" viewBox="0 0 24 24">
                                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                            </div>


                            <div class="mt-2 text-xs text-gray-600">
                                {{ __(role.description) }}
                            </div>

                        </div>

                    </button>
                </div>
            </div>
        </template>


        <template #footer>

            <SecondaryButton @click="currentlyManagingRole = false">
                {{ __('Cancel') }}
            </SecondaryButton>

            <SecondaryButton class="ml-2 bg-blue text-white"
                :class="{ 'opacity-25': updateRoleForm.processing }"
                @click="updateRole"
                :disabled="updateRoleForm.processing">
                {{ __('Save') }}
            </SecondaryButton>

        </template>

    </dialog-modal>


    <confirmation-modal :show="teamMemberBeingRemoved" @close="teamMemberBeingRemoved = null">

        <template #title>
            {{ __('Remove user') }}
        </template>


        <template #content>
            {{ __('Are you sure you would like to remove this person from the company?') }}
        </template>


        <template #footer>

            <secondary-button @click="teamMemberBeingRemoved = null">
                {{ __('Cancel') }}
            </secondary-button>

            <danger-button class="ml-2"
                @click="removeTeamMember"
                :class="{ 'opacity-25': removeTeamMemberForm.processing }"
                :disabled="removeTeamMemberForm.processing">
                {{ __('Remove') }}
            </danger-button>

        </template>

    </confirmation-modal>

</template>

<script>

import { defineComponent } from 'vue'
import FormSection from "../../../Components/FormSection";
import ConfirmationModal from '../../../Components/ConfirmationModal.vue'
import DialogModal from '../../../Components/DialogModal.vue'
import DangerButton from '../../../Components/DangerButton.vue'
import SecondaryButton from '../../../Components/SecondaryButton.vue'
import Button from "../../../Components/Button";

export default defineComponent({

    components: {
        Button,
        FormSection,
        ConfirmationModal,
        DialogModal,
        DangerButton,
        SecondaryButton
    },

    props: {
        team: {
            type: Object
        },
        availableRoles: {
            type: Array,
            default: () => {
                return [];
            }
        }
    },

    data() {
        return {

            updateRoleForm: this.$inertia.form({
                role: null,
            }),

            removeTeamMemberForm: this.$inertia.form(),

            currentlyManagingRole: false,

            managingRoleFor: null,

            teamMemberBeingRemoved: null,

        }
    },


    computed: {

        members() {

            return this.team.users;

        }

    },


    methods: {

        format_language(language_id) {

            return {
                da: this.__('Danish'),
                en: this.__('English'),
            }[ language_id ];

        },

        cancelTeamInvitation(invitation) {

            this.$inertia.delete(route('team-invitations.destroy', invitation), {
                preserveScroll: true
            });

        },


        manageRole(teamMember) {

            this.managingRoleFor = teamMember

            this.updateRoleForm.role = teamMember.membership.role

            this.currentlyManagingRole = true

        },


        updateRole() {

            this.updateRoleForm.put(route('team-members.update', [this.team, this.managingRoleFor]), {
                preserveScroll: true,
                onSuccess: () => (this.currentlyManagingRole = false),
            });

        },


        confirmTeamMemberRemoval(teamMember) {

            this.teamMemberBeingRemoved = teamMember

        },


        removeTeamMember() {

            this.removeTeamMemberForm.delete(route('team-members.destroy', [this.team, this.teamMemberBeingRemoved]), {
                errorBag: 'removeTeamMember',
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => (this.teamMemberBeingRemoved = null),
            });

        },


        displayableRole(role) {

            return this.availableRoles.find(r => r.key === role).name;

        },


        format_created_at( timestamp ) {

            return moment(timestamp).format('DD/MM/YYYY');

        }

    }

});

</script>

<style scoped>

</style>
